import { Route, Navigate } from "react-router";
import PermissionWrapper from "./PermissionWrapper";
import AsyncComponent from "./AsyncComponent";
import {
    APPOINTMENTS_ROUTE,
    DOCUMENT_TYPE_AVIMARK_EXPORT_ROUTE,
    ENDPOINTS,
    INDIVDUAL_DOCUMENT_TYPE_ROUTE_ADVANCED,
} from "../common/utils/url-registry";
import { REVIEW_SUMMARY_PATH } from "@features/review-mode/paths";
import { VITE_PUBLIC_ENV } from "@common/utils/constants";
import SelectClinic from "@/features/auth/SelectClinic";
import CorporateLayout from "@features/corporate/CorporateLayout";
import BridgeAuthCallback from "@/features/auth/BridgeAuthCallback";
import NotFoundPage from "../common/components/NotFound";
import TestErrorRoute, { isTestErrorRouteEnabled, TEST_ERROR_ROUTE } from "./TestErrorRoute";

function AuthenticatedRoutes() {
    return (
        <>
            <Route path={ENDPOINTS.SelectClinic} handle={{ title: "Select Clinic" }} element={<SelectClinic />} />
            <Route
                path="/corporate"
                handle={{ title: "Corporate" }}
                element={
                    <CorporateLayout>
                        <AsyncComponent loadComponent={() => import("@features/corporate/CorporatePage")} />
                    </CorporateLayout>
                }
            />
            <Route
                index
                path={ENDPOINTS.Changelog}
                handle={{ title: "What's New" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@/features/release-notes/Changelog")} />
                    </PermissionWrapper>
                }
            />
            <Route
                index
                path="/"
                // dynamicTitle (not a static title) because the title depends on the ?page=
                // view — "Visits" vs "Calendar" — and toggles in-page, which TitleManager
                // (keyed on route matches, not location.search) can't express. Visit sets it
                // via useDocumentTitle once mounted; until then TitleManager shows bare
                // "HappyDoc" so the prior route's title isn't stranded during the lazy load.
                handle={{ dynamicTitle: true }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/visits/Visit")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path="/dashboard"
                handle={{ title: "Dashboard" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/usage/UsagePage")} />
                    </PermissionWrapper>
                }
            />
            <Route
                index
                path={ENDPOINTS.SignUp}
                handle={{ title: "Check Your Email" }}
                element={<AsyncComponent loadComponent={() => import("@common/components/CheckEmail")} />}
            />
            <Route
                path={ENDPOINTS.EmailVerified}
                handle={{ title: "Email Verified" }}
                element={<AsyncComponent loadComponent={() => import("@features/auth/EmailVerified")} />}
            />
            <Route path={ENDPOINTS.ResendVerificationEmail} element={<Navigate to="/" replace />} />
            <Route path={ENDPOINTS.ResetPassword} element={<Navigate to="/" replace />} />
            <Route
                path={ENDPOINTS.UpdatePassword}
                handle={{ title: "Password" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/auth/UpdatePassword")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path={ENDPOINTS.AcceptInvite}
                handle={{ title: "Accept Invite" }}
                element={<AsyncComponent loadComponent={() => import("@features/auth/AcceptInvite")} />}
            />
            <Route
                path={ENDPOINTS.Calendar}
                handle={{ title: "Calendar" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@common/components/NotFound")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path={APPOINTMENTS_ROUTE}
                handle={{ dynamicTitle: true }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/patient-view/PatientView")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path={REVIEW_SUMMARY_PATH}
                handle={{ title: "Review complete" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/review-mode/ReviewSummary")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path={ENDPOINTS.DocumentBuilder}
                handle={{ title: "Document Builder" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent
                            loadComponent={() => import("../features/document-types/document-builder/BuilderPage")}
                        />
                    </PermissionWrapper>
                }
            />
            <Route
                path={INDIVDUAL_DOCUMENT_TYPE_ROUTE_ADVANCED}
                handle={{ dynamicTitle: true }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent
                            loadComponent={() =>
                                import("../features/document-types/advanced-builder/IndividualDocType")
                            }
                        />
                    </PermissionWrapper>
                }
            />
            <Route
                path={DOCUMENT_TYPE_AVIMARK_EXPORT_ROUTE}
                handle={{ title: "AVImark export" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent
                            loadComponent={() => import("../features/avimark-export/AvimarkExportEditorPage")}
                        />
                    </PermissionWrapper>
                }
            />
            <Route
                path={`/${ENDPOINTS.BridgeAuth}`}
                handle={{ title: "Authorizing" }}
                element={<BridgeAuthCallback />}
            />
            <Route
                path={ENDPOINTS.Settings}
                handle={{ title: "Settings" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("../features/settings/Settings")} />
                    </PermissionWrapper>
                }
            />
            {/* HappyBridge install wizard. The flow itself redirects to Settings → Integrations
                unless account_settings.happy_bridge_beta or account_settings.bridge_upgrade_offer
                is true (EN-1063; see useBridgeUpgradeOffer), so the gate lives with the
                component rather than here.
                See specs/2026-07-29-happy-bridge-wizard-wiring-spec.md */}
            <Route
                path={ENDPOINTS.BridgeSetup}
                handle={{ title: "Set up HappyBridge" }}
                /* PIMS-neutral on purpose: this static handle paints the tab before the flow
                   mounts and knows which PIMS the account runs. `BridgeSetupFlow` replaces it
                   with "Connect <PIMS>" via useDocumentTitle as soon as that resolves. */
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@features/bridge-setup/BridgeSetupFlow")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path={ENDPOINTS.AvimarkExamSetup}
                handle={{ title: "AVImark exam setup" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent
                            loadComponent={() => import("../features/avimark-export/AvimarkExamSetupPage")}
                        />
                    </PermissionWrapper>
                }
            />
            <Route
                path="/admin/impersonation"
                handle={{ title: "Impersonation" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("../features/admin/AdminImpersonation")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path="/admin/checkout"
                handle={{ title: "Checkout" }}
                element={
                    <PermissionWrapper>
                        <AsyncComponent loadComponent={() => import("@/features/admin/checkout/SalesCheckoutTool")} />
                    </PermissionWrapper>
                }
            />
            <Route
                path="/checkout/:session_id"
                handle={{ title: "Checkout" }}
                element={<AsyncComponent loadComponent={() => import("@/features/checkout/CustomerCheckoutPage")} />}
            />
            <Route
                path="/checkout/:session_id/success"
                handle={{ title: "Checkout Complete" }}
                element={<AsyncComponent loadComponent={() => import("@/features/checkout/SuccessPage")} />}
            />
            {/* Dev-only: lock screen preview (PR 3). Remove or guard with feature flag for production. */}
            {import.meta.env.DEV && (
                <Route
                    path="/preview-lock"
                    handle={{ title: "Lock Screen Preview" }}
                    element={
                        <AsyncComponent loadComponent={() => import("@/features/lock-screen/LockScreenPreview")} />
                    }
                />
            )}
            {isTestErrorRouteEnabled(VITE_PUBLIC_ENV) && (
                <Route path={TEST_ERROR_ROUTE} handle={{ title: "Test Error" }} element={<TestErrorRoute />} />
            )}
            <Route path="*" handle={{ title: "Not Found" }} element={<NotFoundPage />} />
        </>
    );
}

export default AuthenticatedRoutes;
